'use client';

import { useTranslations } from 'next-intl';
import { H2, LeftSideTextWrapper, P, PLarge } from './StyleConstants';
import { Container } from '..';
import { CustomImage as Image } from 'components/shared';
import { useRef } from 'react';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import gsap from 'gsap';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from '../../hooks/animation/useGsapContext';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { analytics } from 'lib/analytics/analytics';
import CustomLink from 'components/shared/CustomLink';
import styles from './HealthSpanSection.module.css';

const Ogdb = () => {
  const t = useTranslations('home');
  return (
    <div className={`${styles.ogdbRoot} .ogdb`}>
      <div className={styles.ogdbContent}>
        <p className="ogdb">
          {/* Find Glucose response for your favourite foods */}
          {t('home.healthSpanSection.ogdb.description')}
        </p>
        <CustomLink
          onClick={() => analytics.track('link Ogdb clicked', { location: '/' })}
          data-link="ogdb"
          href="/ogdb"
          className="ogdb"
        >
          {/* Explore OGDb */}
          {t('home.healthSpanSection.ogdb.cta')}
        </CustomLink>
      </div>
    </div>
  );
};

export const HealthSpanSection = ({
  className,
  title,
  btnText,
  description,
  imageMobile,
  startAtTopTop,
  titleLarge,
  textColor,
  ctaLink,
  analyticEvent,
}: {
  className?: string;
  title?: string | React.ReactNode;
  btnText?: string | React.ReactNode;
  description?: string;
  imageMobile: string;
  startAtTopTop?: boolean;
  titleLarge?: boolean;
  textColor?: string;
  ctaLink?: string;
  analyticEvent?: string;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRefD = useRef<HTMLImageElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const textContent = useRef<HTMLDivElement>(null);
    const { width } = useWindowSize();
    const gsapContext = useGsapContext();
    const { add } = useMasterTimeline();

    const animationInit = () => {
      // Both images are always mounted now; keep animations desktop-only as before
      if (!imageRefD.current || width <= 924) return;
      gsapContext.add(() => {
        const tl = gsap.timeline({
          defaults: { duration: 1 },
          scrollTrigger: {
            trigger: containerRef.current,
            scrub: true,
            start: 'top bottom',
            end: 'bottom 40%',
            invalidateOnRefresh: true,
          },
        });

        // image animation
        tl.fromTo(
          imageRefD.current,
          {
            scale: 0.25,
          },
          {
            duration: 0.75,
            scale: 1,
          },
        );
        if (startAtTopTop) {
          gsap.set(textContent.current, {
            opacity: 1,
          });
        }

        // text animation
        if (textContent.current && !startAtTopTop) {
          tl.fromTo(
            textContent.current,
            {
              opacity: 0,
              y: 50,
            },
            {
              duration: 0.75,
              opacity: 1,
              y: 0,
            },
          );
        }

        // dim
        tl.fromTo(
          containerRef.current,
          {
            filter: 'brightness(1)',
          },
          {
            filter: 'brightness(0.2)',
            duration: 0.25,
          },
        );

        add(tl, 'health-span-section', 5);
      });
    };

    useIsomorphicLayoutEffect(() => {
      const animTimer = setTimeout(animationInit, 100);
      return () => {
        if (animTimer) clearTimeout(animTimer);
      };
    }, [width]);

    return (
      <section
        className={className ? `${styles.root} ${className}` : styles.root}
        ref={containerRef}
        style={{ color: textColor }}
      >
        <Image
          className={`${styles.imgD} ${styles.imgDesktop}`}
          src={'web_v2/backgrounds/m1-scan.png'}
          alt="healthspan-desktop"
          fill
          ref={imageRefD}
        />
        <Image
          className={`${styles.imgD} ${styles.imgMobile}`}
          src={imageMobile}
          alt="healthspan-mbl"
          fill
          ref={imageRef}
        />
        <Container>
          {titleLarge ? (
            <LeftSideTextWrapper ref={textContent} className={styles.w75}>
              {title && <H2>{title}</H2>}
              {description && <P>{description}</P>}
              {ctaLink && (
                <CustomLink
                  onClick={() => {
                    analyticEvent && analytics.track(analyticEvent);
                  }}
                  href={{ pathname: ctaLink }}
                >
                  {btnText}
                </CustomLink>
              )}
            </LeftSideTextWrapper>
          ) : (
            <LeftSideTextWrapper ref={textContent} className={styles.w60}>
              {ctaLink && (
                <CustomLink href={{ pathname: ctaLink }}>{btnText}</CustomLink>
              )}
              {description && <PLarge>{description}</PLarge>}
              {ctaLink && (
                <CustomLink
                  onClick={() => {
                    analyticEvent && analytics.track(analyticEvent);
                  }}
                  href={ctaLink}
                >
                  {btnText}
                </CustomLink>
              )}
            </LeftSideTextWrapper>
          )}
        </Container>
        <Ogdb />
      </section>
    );
};
